/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * The UE Editor uses the same regex to determine valid href navigateTo values.
 * If this regex is modified consider if the UE editor needs updating as well.
 * Andrea Botti will need to keep in sync.
 */
export const epicOriginRegex =
	/^((www|www2|localhost|rossi|dev|docs|forums|company|legal|status|onlineservices)(\.staging)?\.)?(unrealengine|epicgames|twinmotion|metahuman|realityscan|kidswebservices|fab|quixel)(\.com|-(ci|gamedev)\.ol\.epicgames\.net)\/?/;

/**
 * Checks if href is a safe relative path (starts with / but not //).
 * Rejects paths containing backslash - e.g. /\evil.com normalizes to //evil.com
 * (protocol-relative URL) in browsers, enabling open redirect bypass.
 */
export const isRelative = (href?: string): boolean => {
	return Boolean(href?.startsWith("/")) && !href?.startsWith("//") && !href?.includes("\\");
};

export const isHash = (href?: string): boolean => {
	return Boolean(href?.startsWith("#"));
};

export const isSameOrigin = (href = ""): boolean => {
	if (!href) return false;

	// Allow relative URLs (starting with / but not //)
	if (isRelative(href)) {
		return true;
	}

	// Allow hash URLs (starting with #)
	if (isHash(href)) {
		return true;
	}

	// For absolute URLs, only allow http:// or https:// protocols
	// Reject all other protocols (javascript:, data:, vbscript:, etc.)
	try {
		let cleanedHref = href;
		// Handle protocol-relative URLs (starting with //)
		if (cleanedHref.startsWith("//")) {
			cleanedHref = `https:${cleanedHref}`;
		}

		const url = new URL(cleanedHref);

		// Only allow http and https protocols - reject all others
		// This automatically blocks javascript:, data:, vbscript:, file:, etc.
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			return false;
		}

		const matches = url.hostname.match(epicOriginRegex);
		const sameOrigin = (matches && matches.length && matches[0].length === url.hostname.length) || false;
		return sameOrigin;
	} catch {
		return false;
	}
};

/**
 * Checks if a URL is absolute (starts with http/https or protocol-relative //)
 * @param href - The URL to check
 * @returns boolean - True if URL is absolute
 */
export const isAbsolute = (href?: string): boolean => {
	return Boolean(href?.indexOf("http") === 0) || Boolean(href?.startsWith("//"));
};

/**
 * Checks if a URL is a mailto link
 * @param href - The URL to check
 * @returns boolean - True if URL is a mailto link
 */
export const isMailTo = (href?: string): boolean => {
	return Boolean(href?.indexOf("mailto:") === 0);
};

/**
 * Checks if a URL is an Epic Games Launcher link
 * @param href - The URL to check
 * @returns boolean - True if URL is a launcher link
 */
export const isLauncher = (href?: string): boolean => {
	return Boolean(href?.indexOf("com.epicgames.launcher:") === 0);
};

/**
 * Checks if a pathname contains a locale prefix
 * @param locales - Array of locale codes to check for
 * @param href - The URL/pathname to check
 * @returns boolean - True if pathname has a locale prefix
 */
export const pathnameHasLocale = (locales: string[], href?: string): boolean => {
	if (!href || !locales?.length) return false;

	if (href.startsWith("/api/")) return true;

	return locales.some((locale) => href.startsWith(`/${locale}/`) || href === `/${locale}`);
};

/**
 * Strips a basePath prefix from a pathname so that locale processing can
 * operate on the route path alone.
 *
 * Sites served under a sub-path (e.g. Fab at `/o`) include the basePath in
 * resolved anchor hrefs.  Without stripping it first, locale insertion
 * produces `/en-US/o/page` instead of the correct `/o/en-US/page`.
 *
 * @param pathname - Full pathname that may start with basePath (e.g. "/o/become-a-publisher")
 * @param basePath - The site basePath to strip (e.g. "/o"). Empty string means no basePath.
 * @returns `{ routePath, basePath }` where routePath always starts with "/"
 *
 * @example
 * stripBasePath("/o/become-a-publisher", "/o")
 * // => { routePath: "/become-a-publisher", basePath: "/o" }
 *
 * @example
 * stripBasePath("/become-a-publisher", "")
 * // => { routePath: "/become-a-publisher", basePath: "" }
 */
export const stripBasePath = (pathname: string, basePath: string): { routePath: string; basePath: string } => {
	if (basePath && pathname.startsWith(`${basePath}/`)) {
		return { routePath: pathname.slice(basePath.length), basePath };
	}
	if (basePath && pathname === basePath) {
		return { routePath: "/", basePath };
	}
	return { routePath: pathname, basePath: "" };
};

/**
 * Function signature for locale-aware path generation
 */
export type GetLocalizedPathFn = (path: string, locale: string) => string;

/**
 * Default locale path generation - always prefix with locale.
 * Used as fallback when no custom getLocalizedPath is provided.
 */
export const defaultLocalePath: GetLocalizedPathFn = (path, locale) => {
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return `/${locale}${normalizedPath}`;
};

/**
 * Normalizes a URL href with locale prefix based on the site's locale configuration.
 * Consolidates the common pattern of checking isRelative + pathnameHasLocale + getLocalizedPath.
 *
 * @param href - The href to normalize (can contain :locale or {locale} placeholders)
 * @param locale - Current locale code (e.g., "de", "en-US")
 * @param locales - Array of supported locale codes
 * @param getLocalizedPath - Optional custom path generator (defaults to always-prefix behavior)
 * @returns The normalized href with appropriate locale prefix
 *
 * @example
 * // Default behavior (always prefix)
 * normalizeLocaleHref("/about", "de", ["en-US", "de"]) // => "/de/about"
 *
 * @example
 * // With "as-needed" mode via custom getLocalizedPath
 * normalizeLocaleHref("/about", "en-US", ["en-US", "de"], asNeededPath) // => "/about"
 *
 * @example
 * // With locale placeholder
 * normalizeLocaleHref("/:locale/about", "de", ["en-US", "de"]) // => "/de/about"
 */
export const normalizeLocaleHref = (
	href: string,
	locale: string,
	locales: string[],
	getLocalizedPath: GetLocalizedPathFn = defaultLocalePath,
): string => {
	if (!href) return href;

	// Replace locale placeholders
	let finalHref = href.replace(/:locale/g, locale).replace(/\{locale\}/g, locale);

	// Add locale prefix if relative and doesn't already have one
	if (isRelative(finalHref) && !pathnameHasLocale(locales, finalHref)) {
		finalHref = getLocalizedPath(finalHref, locale);
	}

	return finalHref;
};

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns boolean - True if URL is valid
 */
export const isValidUrl = (url?: string | URL | null): boolean => {
	if (!url) return false;
	try {
		new URL(url);
		return true;
	} catch {
		console.error("Invalid URL: ", url);
		return false;
	}
};

/**
 * Checks if a URL uses HTTPS protocol
 * @param url - The URL to check
 * @returns boolean - True if URL uses HTTPS
 */
export const isHttpsUrl = (url?: string | URL | null): boolean => {
	if (!url || !isValidUrl(url)) return false;
	if (new URL(url).protocol !== "https:") return false;
	return true;
};

/**
 * Checks if a URL matches the configured server origin
 * @param href - The URL to check
 * @returns boolean - True if URL matches server origin
 */
export const isOriginServerUrl = (href: string): boolean => {
	try {
		// Check if it's an absolute URL
		const url = new URL(href);

		// Get the configured server URL origin
		const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;
		if (serverUrl) {
			const serverOrigin = new URL(serverUrl).origin;
			// If the URL's origin matches the server origin, return true
			if (url.origin === serverOrigin) {
				return true;
			}
		}

		// It's an absolute URL that doesn't match our origin
		return false;
	} catch {
		// If URL parsing fails, not a valid absolute URL
		return false;
	}
};
